import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { savePackingList, updatePackingList, deletePackingList } from '../services/packingListService';
import { queryKeys } from '../constants/queryKeys';

/**
 * useSavePackingList — Mutation hook to save a new packing list
 *
 * Usage:
 *   const saveMutation = useSavePackingList();
 *   saveMutation.mutate(formData, {
 *     onSuccess: (data) => {
 *       // handle success popup or reset
 *     }
 *   });
 */
export const useSavePackingList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => savePackingList(data),

    onSuccess: (resData) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.packingList.all });
      toast.success('Packing list saved successfully!');
    },

    onError: (error) => {
      const message = error?.response?.data?.message || 'Failed to save packing list.';
      toast.error(message);
    },
  });
};

/**
 * useUpdatePackingList — Mutation hook to update an existing packing list
 *
 * Usage:
 *   const updateMutation = useUpdatePackingList();
 *   updateMutation.mutate({ id, data: formData }, {
 *     onSuccess: (data) => {
 *       // handle success popup
 *     }
 *   });
 */
export const useUpdatePackingList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => updatePackingList(id, data),

    onSuccess: (resData, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.packingList.all });
      toast.success('Packing list updated successfully!');
    },

    onError: (error) => {
      const message = error?.response?.data?.message || 'Failed to update packing list.';
      toast.error(message);
    },
  });
};

/**
 * useDeletePackingList — Delete a packing list
 *
 * Usage:
 *   const deleteMutation = useDeletePackingList();
 *   deleteMutation.mutate(packingListId);
 */
export const useDeletePackingList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => deletePackingList(id),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.packingList.all });
      toast.success('Packing list deleted successfully!');
    },

    onError: (error) => {
      const message = error?.response?.data?.message || 'Failed to delete packing list. Please try again.';
      toast.error(message);
    },
  });
};
